import { HttpRequestParameters } from "../../../Primitives/Http.js";
import { Stream } from "node:stream";
import {
    ConnectionStringType,
    SqlConnectionString,
    RavenConnectionString,
    OlapConnectionString, ElasticSearchConnectionString, QueueConnectionString
} from "../Etl/ConnectionString.js";
import { AiConnectionString } from "../AI/ConnectionStrings/AiConnectionString.js";
import { DocumentConventions } from "../../Conventions/DocumentConventions.js";
import { OperationResultType, IMaintenanceOperation } from "../OperationAbstractions.js";
import { RavenCommand } from "../../../Http/RavenCommand.js";
import { ServerNode } from "../../../Http/ServerNode.js";

export interface GetConnectionStringsResult {
    ravenConnectionStrings: Record<string, RavenConnectionString>;
    sqlConnectionStrings: Record<string, SqlConnectionString>;
    olapConnectionStrings: Record<string, OlapConnectionString>;
    elasticSearchConnectionStrings: Record<string, ElasticSearchConnectionString>;
    queueConnectionStrings: Record<string, QueueConnectionString>;
    aiConnectionStrings: Record<string, AiConnectionString>;
}

export class GetConnectionStringsOperation implements IMaintenanceOperation<GetConnectionStringsResult> {
    private readonly _connectionStringName: string;
    private readonly _type: ConnectionStringType;

    public constructor()
    public constructor(connectionStringName: string, type: ConnectionStringType)
    public constructor(connectionStringName?: string, type?: ConnectionStringType) {
        this._connectionStringName = connectionStringName;
        this._type = type;
    }

    getCommand(conventions: DocumentConventions): RavenCommand<GetConnectionStringsResult> {
        return new GetConnectionStringCommand(this._connectionStringName, this._type);
    }

    public get resultType(): OperationResultType {
        return "CommandResult";
    }
}

export class GetConnectionStringCommand extends RavenCommand<GetConnectionStringsResult> {
    private readonly _connectionStringName: string;
    private readonly _type: ConnectionStringType;

    public constructor(connectionStringName: string, type: ConnectionStringType) {
        super();
        this._connectionStringName = connectionStringName;
        this._type = type;
    }

    get isReadRequest(): boolean {
        return true;
    }

    createRequest(node: ServerNode): HttpRequestParameters {
        let uri = node.url + "/databases/" + node.database + "/admin/connection-strings";

        if (this._connectionStringName) {
            uri += "?connectionStringName=" + encodeURIComponent(this._connectionStringName) + "&type=" + this._type;
        }

        return {
            method: "GET",
            uri
        };
    }

    public async setResponseAsync(bodyStream: Stream, fromCache: boolean): Promise<string> {
        if (!bodyStream) {
            return;
        }

        let body = "";
        this.result = await this._defaultPipeline(_ => body += _).process(bodyStream);

        if (this.result.ravenConnectionStrings) {
            this.result.ravenConnectionStrings = Object.entries(this.result.ravenConnectionStrings)
                .reduce(((previousValue, currentValue) => {
                    previousValue[currentValue[0]] = Object.assign(new RavenConnectionString(), currentValue[1]);
                    return previousValue;
                }), {} as Record<string, RavenConnectionString>);
        }

        if (this.result.sqlConnectionStrings) {
            this.result.sqlConnectionStrings = Object.entries(this.result.sqlConnectionStrings)
                .reduce(((previousValue, currentValue) => {
                    previousValue[currentValue[0]] = Object.assign(new SqlConnectionString(), currentValue[1]);
                    return previousValue;
                }), {} as Record<string, SqlConnectionString>);
        }

        if (this.result.elasticSearchConnectionStrings) {
            this.result.elasticSearchConnectionStrings = Object.entries(this.result.elasticSearchConnectionStrings)
                .reduce(((previousValue, currentValue) => {
                    previousValue[currentValue[0]] = Object.assign(new ElasticSearchConnectionString(), currentValue[1]);
                    return previousValue;
                }), {} as Record<string, ElasticSearchConnectionString>);
        }

        if (this.result.queueConnectionStrings) {
            this.result.queueConnectionStrings = Object.entries(this.result.queueConnectionStrings)
                .reduce(((previousValue, currentValue) => {
                    previousValue[currentValue[0]] = Object.assign(new QueueConnectionString(), currentValue[1]);
                    return previousValue;
                }), {} as Record<string, QueueConnectionString>);
        }

        if (this.result.olapConnectionStrings) {
            this.result.olapConnectionStrings = Object.entries(this.result.olapConnectionStrings)
                .reduce(((previousValue, currentValue) => {
                    previousValue[currentValue[0]] = Object.assign(new OlapConnectionString(), currentValue[1]);
                    return previousValue;
                }), {} as Record<string, OlapConnectionString>);
        }

        if (this.result.aiConnectionStrings) {
            this.result.aiConnectionStrings = Object.entries(this.result.aiConnectionStrings)
                .reduce(((previousValue, currentValue) => {
                    previousValue[currentValue[0]] = Object.assign(new AiConnectionString(), currentValue[1]);
                    return previousValue;
                }), {} as Record<string, AiConnectionString>);
        }

        return body;
    }
}
